import React, { useId, useState } from 'react';

export interface PricingFaqItem {
  q: string;
  a: string;
}

interface PricingFaqAccordionProps {
  faqs: PricingFaqItem[];
  /** Accent text class for open chevron / focus ring (e.g. productTheme text) */
  accentTextClassName?: string;
}

/**
 * Accessible FAQ accordion with keyboard support and ARIA attributes.
 * Only one panel is open at a time to keep the section easy to scan.
 */
const PricingFaqAccordion: React.FC<PricingFaqAccordionProps> = ({
  faqs,
  accentTextClassName = 'text-accent-600 dark:text-accent-400',
}) => {
  const baseId = useId();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="space-y-3" role="list">
      {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        const panelId = `${baseId}-panel-${index}`;
        const buttonId = `${baseId}-button-${index}`;

        return (
          <div
            key={faq.q}
            role="listitem"
            className="card overflow-hidden transition-shadow duration-200 hover:shadow-md"
          >
            <h3 className="m-0">
              <button
                id={buttonId}
                type="button"
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-900"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(index)}
              >
                <span className="text-slate-900 dark:text-white font-semibold pr-2">
                  {faq.q}
                </span>
                <svg
                  className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${accentTextClassName} ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className={isOpen ? 'px-5 pb-5' : undefined}
            >
              {isOpen && (
                <p className="text-slate-600 dark:text-dark-400 leading-relaxed">{faq.a}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PricingFaqAccordion;
